import React, { JSX, useContext } from 'react';
import { twMerge } from 'tailwind-merge';

import { Icon } from '@videoblocks/design-system';

import { NavSearchContext } from '../../context/NavSearchContext';
import ConversationalSearchTooltip from './ConversationalSearchTooltip';

const ICON_CLS = 'relative z-10 w-12 self-stretch flex justify-center items-center transition-opacity duration-300';

export default function SearchWithAIButton(): JSX.Element {
    const { conversationalSearchEnabled, setConversationalSearchEnabled: setEnabled } = useContext(NavSearchContext);

    const toggleButton = (
        <button
            data-testid="conversational-search-toggle"
            type="button"
            onClick={() => setEnabled((prev) => !prev)}
            aria-pressed={conversationalSearchEnabled}
            aria-label={conversationalSearchEnabled ? 'Switch to standard search' : 'Switch to AI search'}
            className={twMerge(
                'relative h-14 p-1 bg-linear-to-r rounded-full outline-2 -outline-offset-1 outline-violet-200 inline-flex items-center ml-2 cursor-pointer',
                'transition-colors duration-300 ease-in-out',
                conversationalSearchEnabled
                    ? 'from-yellow-700 via-bubblegum-600 to-blueviolet-500'
                    : 'from-yellow-50 via-fuchsia-100 to-violet-100',
            )}
        >
            {/* Sliding white pill */}
            <div
                className={twMerge(
                    'absolute top-1 bottom-1 w-12 bg-white rounded-full shadow-lg transition-transform duration-300 ease-in-out',
                    conversationalSearchEnabled ? 'translate-x-12' : 'translate-x-0',
                )}
            />
            {/* Search icon */}
            <div className={twMerge(ICON_CLS, conversationalSearchEnabled ? 'opacity-50' : 'opacity-100')}>
                <Icon name="search" width={20} height={20} />
            </div>
            {/* Sparkles icon (inline SVG for gradient fill) */}
            <div className={twMerge(ICON_CLS, conversationalSearchEnabled ? 'opacity-100' : 'opacity-50')}>
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 27 26" width={24} height={24}>
                    <defs>
                        <linearGradient id="sparkle-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                            <stop
                                offset="0%"
                                className="transition-all duration-300"
                                stopColor={
                                    conversationalSearchEnabled
                                        ? 'var(--color-blueviolet-600)'
                                        : 'var(--color-gray-950)'
                                }
                            />
                            <stop
                                offset="100%"
                                className="transition-all duration-300"
                                stopColor={
                                    conversationalSearchEnabled ? 'var(--color-bubblegum-600)' : 'var(--color-gray-950)'
                                }
                            />
                        </linearGradient>
                    </defs>
                    <path
                        fill="url(#sparkle-gradient)"
                        d="M23.25 18a.75.75 0 0 1 .75.75A2.25 2.25 0 0 0 26.25 21a.75.75 0 0 1 0 1.5A2.25 2.25 0 0 0 24 24.75a.75.75 0 0 1-1.5 0 2.25 2.25 0 0 0-2.25-2.25.75.75 0 0 1 0-1.5 2.25 2.25 0 0 0 2.25-2.25.75.75 0 0 1 .75-.75"
                    />
                    <path
                        fill="url(#sparkle-gradient)"
                        d="M9.75 3a.75.75 0 0 1 .75.75A8.25 8.25 0 0 0 18.75 12a.75.75 0 0 1 0 1.5 8.25 8.25 0 0 0-8.25 8.25.75.75 0 0 1-1.5 0A8.25 8.25 0 0 0 .75 13.5a.75.75 0 0 1 0-1.5A8.25 8.25 0 0 0 9 3.75.75.75 0 0 1 9.75 3"
                    />
                    <path
                        fill="url(#sparkle-gradient)"
                        d="M23.25 0a.75.75 0 0 1 .75.75A2.25 2.25 0 0 0 26.25 3a.75.75 0 0 1 0 1.5A2.25 2.25 0 0 0 24 6.75a.75.75 0 0 1-1.5 0 2.25 2.25 0 0 0-2.25-2.25.75.75 0 0 1 0-1.5A2.25 2.25 0 0 0 22.5.75a.75.75 0 0 1 .75-.75"
                    />
                </svg>
            </div>
        </button>
    );

    return (
        <ConversationalSearchTooltip
            enabled={conversationalSearchEnabled}
            setEnabled={setEnabled}
            trigger={toggleButton}
        />
    );
}
